import React, {
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { AppDispatch, RootState } from 'src/app/store';
import { formatTextForSentryLog } from 'src/utils/Formatter';
import { useTranslation } from 'react-i18next';
import { Banner } from '@dsny/dsny-component-library';
import { useStatsigClient } from '@statsig/react-bindings';
import { DELAYED_RADIO_PROCESSING } from 'src/types/FeataureFlags';
import CitiesTracking from './CitiesTracking/CitiesTracking';
import {
  clearSongInsightsData,
  resetStateDashBoard,
  TrackingStatusEnum,
  updateModalStatus,
} from './Dashboard.slice';
import {
  AirplayReportHeading,
  AirplayWrapper,
  DashboardContainer,
  DashboardWrapper,
  NoInsigthsStyle,
  SecondHeaderWrapper,
  SecondRowWrapper,
  SelectedSongWrapper,
  StationLocationWrapper,
  TimelineWrapper,
  UniversalFilterWrapper,
  BannerWrapper,
} from './Dashboard.styles';
import DashboardSkeleton from './DashboardSkeleton';
import Overview from './Overview/Overview';
import SelectedSong from './SelectedSong/SelectedSong';
import SongSelection from './SongSelection/SongSelection';
import StationsTracking from './StationsTracking/StationsTracking';
import UniversalFilter from './UniversalFilter/UniversalFilter';
import ComparisonTimelineTracking from './TimelineTracking/ComparisonTimelineTracking';
import TimelineTracking from './TimelineTracking/TimelineTracking';
import { resetStateAddTrack } from '../Purchase/MultiAsset';
import { EnterprisePlanLimitModal } from '../Enterprise/PlanLimit/EnterprisePlanLimitModal';
import { EnterpriseExtendSongModal } from '../Enterprise/ExtendRenew/EnterpriseExtendSongModal';
import AddNewTrackMobile from './AddNewTrack/AddNewTrackMobile';
import SmartInsightsMobile from '../SmartInsights/SmartInsightsMobile';
import SmartInsightsDesktop from '../SmartInsights/SmartInsightsDesktop';
import { hasInsigthsToRender } from '../SmartInsights/SmartInsightsCardContentHelper';
import { getSongInsights } from './Dashboard.thunks';

const LazyAirplayTracking = lazy(
  () => import('./AirplayTracking/AirplayTracking')
);
const LazyComparisonAirplayTracking = lazy(
  () => import('./AirplayTracking/ComparisonAirplayTracking')
);

const Dashboard: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const [hasComparisonOn, setComparisonOn] = useState(true);
  const [showEnterpriseExtend, setShowEnterpriseExtend] = useState(false);
  const { client } = useStatsigClient();

  const {
    songSelection,
    songOverview,
    songDaily,
    songHeatmap,
    songCities,
    songStations,
    filterSelection,
    modalControl,
    songInsights,
  } = useSelector((state: RootState) => state.dashboard);

  const { isFetchingSongSelection } = songSelection;
  const { isFetchingSongOverview } = songOverview;
  const { isFetchingSongDaily } = songDaily;
  const { isFetchingSongHeatmap } = songHeatmap;
  const { isFetchingSongCities } = songCities;
  const { isFetchingSongStations } = songStations;
  const [isLoading, setLoading] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const minHideTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const showTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const loadingStartRef = useRef<number | null>(null);
  const [wasLoadingRemoved, setWasLoadingRemoved] = useState(false);
  const [isTrackingReport, setIsTrackingReport] = useState(false); // Caster tracking report
  const [isMobileViewport, setIsMobileViewport] = useState(
    window.innerWidth <= 480
  );
  const { selectedSong } = songSelection;
  const queryParams = new URLSearchParams(location.search);
  const releaseId = queryParams.get('release');
  const [cachedReleaseId, setCachedReleaseId] = useState<string | null>(
    releaseId
  );

  useEffect(() => {
    if (releaseId) {
      setCachedReleaseId(releaseId);
    }
  }, [releaseId]);

  useEffect(() => {
    if (
      (selectedSong &&
        selectedSong?.selected_tracking_period?.status ===
          TrackingStatusEnum.ACTIVE.toString()) ||
      selectedSong?.selected_tracking_period?.status ===
        TrackingStatusEnum.EXPIRING.toString()
    ) {
      dispatch(
        getSongInsights({
          songIds: [selectedSong.song_id],
        })
      );
    } else {
      dispatch(clearSongInsightsData());
    }
  }, [dispatch, selectedSong]);

  // Log to Sentry if Loader was removed by the timeout
  useEffect(() => {
    if (wasLoadingRemoved) {
      Sentry.captureException(
        formatTextForSentryLog(
          `Dashboard loader took more than 30 seconds to turn off 
              SongSelection: ${isFetchingSongSelection} SongOverview: ${isFetchingSongOverview} SongDaily: ${isFetchingSongDaily} 
              SongHeatmap: ${isFetchingSongHeatmap} SongCities: ${isFetchingSongCities} SongStations: ${isFetchingSongStations}`
        )
      );
    }
  }, [wasLoadingRemoved]);

  // Handling enterprise limit modal
  const handleShowEnterpriseLimit = (displayEnterpriseLimit: boolean) => {
    dispatch(updateModalStatus(displayEnterpriseLimit));
  };

  // Handling loader max time
  const handleLoaderMaxTime = useCallback(() => {
    return setTimeout(() => {
      setLoading(false);
      setWasLoadingRemoved(true);
    }, 30000); // 30 seconds delay
  }, []);

  const isFetchingAny = useMemo(() => {
    return (
      isFetchingSongSelection ||
      isFetchingSongOverview ||
      isFetchingSongDaily ||
      isFetchingSongHeatmap ||
      isFetchingSongCities ||
      isFetchingSongStations
    );
  }, [
    isFetchingSongSelection,
    isFetchingSongOverview,
    isFetchingSongDaily,
    isFetchingSongHeatmap,
    isFetchingSongCities,
    isFetchingSongStations,
  ]);

  useEffect(() => {
    const minLoaderMs = 300;
    const showDelayMs = 200;

    if (isFetchingAny) {
      if (minHideTimeoutRef.current) {
        clearTimeout(minHideTimeoutRef.current);
        minHideTimeoutRef.current = null;
      }

      if (!isLoading) {
        if (!showTimeoutRef.current) {
          showTimeoutRef.current = setTimeout(() => {
            if (isFetchingAny) {
              loadingStartRef.current = Date.now();
              setLoading(true);
              timeoutRef.current = handleLoaderMaxTime();
            }
            showTimeoutRef.current = null;
          }, showDelayMs);
        }
      } else if (!loadingStartRef.current) {
        loadingStartRef.current = Date.now();
      }
      return;
    }

    if (showTimeoutRef.current) {
      clearTimeout(showTimeoutRef.current);
      showTimeoutRef.current = null;
    }

    if (!isLoading) {
      loadingStartRef.current = null;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      return;
    }

    const startedAt = loadingStartRef.current ?? Date.now();
    const elapsedMs = Date.now() - startedAt;
    const remainingMs = minLoaderMs - elapsedMs;

    const completeHide = () => {
      setLoading(false);
      loadingStartRef.current = null;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };

    if (remainingMs > 0) {
      minHideTimeoutRef.current = setTimeout(completeHide, remainingMs);
      return;
    }

    completeHide();
  }, [handleLoaderMaxTime, isFetchingAny, isLoading]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (minHideTimeoutRef.current) {
        clearTimeout(minHideTimeoutRef.current);
      }
      if (showTimeoutRef.current) {
        clearTimeout(showTimeoutRef.current);
      }
    };
  }, []);

  // Get available promotions
  useEffect(() => {
    dispatch(resetStateAddTrack());
  }, []);

  useEffect(() => {
    if (
      songSelection?.songs?.length === 0 &&
      songSelection?.expiredSongs?.length === 0
    ) {
      dispatch(resetStateDashBoard());
      navigate('/payment/multiasset');
    }
  }, [songSelection, filterSelection]);

  useEffect(() => {
    setIsTrackingReport(location.pathname === '/trackingreport');
  }, [location.pathname]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobileViewport(window.innerWidth <= 480);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Caching heatmap
  const MemoizedLazyAirplayTracking = useMemo(() => {
    return LazyAirplayTracking;
  }, []);
  const MemoizedLazyComparisonAirplayTracking = useMemo(
    () => LazyComparisonAirplayTracking,
    []
  );

  // Processing spins banner message
  const processingSpinsMessage = {
    title: t('DASHBOARD_PROCESSING_SPINS_TITLE'),
    description: t('DASHBOARD_PROCESSING_SPINS_DESC'),
  };

  return (
    <DashboardWrapper $isTrackingReport={isTrackingReport}>
      {/* Second header */}
      <SecondHeaderWrapper $isTrackingReport={isTrackingReport}>
        {/* Banner message */}
        {client.checkGate(DELAYED_RADIO_PROCESSING) && (
          <BannerWrapper>
            <Banner
              bannerContent={processingSpinsMessage}
              variant="warning"
              width="100%"
            />
          </BannerWrapper>
        )}

        {/* Airplay report heading */}
        {!isTrackingReport && (
          <AirplayReportHeading>
            {t('DASHBOARD_AIRPLAY_REPORT_HEADING')}
          </AirplayReportHeading>
        )}

        {/* Add new track button - mobile */}
        <AddNewTrackMobile setShowEnterpriseLimit={handleShowEnterpriseLimit} />
        <SecondRowWrapper>
          {/* Song combobox */}
          <SongSelection
            releaseId={cachedReleaseId}
            isTrackingReport={isTrackingReport}
          />
          {/* Universal filter */}
          <UniversalFilterWrapper>
            <UniversalFilter
              hasComparisonOn={hasComparisonOn}
              setComparisonOn={setComparisonOn}
            />
          </UniversalFilterWrapper>
        </SecondRowWrapper>
      </SecondHeaderWrapper>

      {/* Dashboard container */}
      <DashboardContainer $isTrackingReport={isTrackingReport}>
        {isLoading && (
          <DashboardSkeleton
            isTrackingReport={isTrackingReport}
            isMobileViewport={isMobileViewport}
          />
        )}

        {/* Song info */}
        <SelectedSongWrapper>
          <SelectedSong
            isTrackingReport={isTrackingReport}
            setShowEnterpriseLimit={handleShowEnterpriseLimit}
            setShowEnterpriseExtend={setShowEnterpriseExtend}
          />

          {/* Smart insights - mobile */}
          {hasInsigthsToRender(songInsights?.songInsightsData) && (
            <SmartInsightsMobile />
          )}

          {/* Overview */}
          <Overview
            hasComparisonOn={hasComparisonOn}
            isTrackingReport={isTrackingReport}
          />
        </SelectedSongWrapper>

        {/* Smart Insights — desktop */}
        {hasInsigthsToRender(songInsights?.songInsightsData) ? (
          <SmartInsightsDesktop />
        ) : (
          <NoInsigthsStyle />
        )}

        {/* Line chart */}
        <TimelineWrapper>
          {hasComparisonOn ? (
            <ComparisonTimelineTracking />
          ) : (
            <TimelineTracking />
          )}
        </TimelineWrapper>

        {/* Stations and city list */}
        <StationLocationWrapper>
          <StationsTracking hasComparisonOn={hasComparisonOn} />
          <CitiesTracking hasComparisonOn={hasComparisonOn} />
        </StationLocationWrapper>

        {/* Heat-map chart */}
        <AirplayWrapper>
          <Suspense fallback={<div>...</div>}>
            {hasComparisonOn ? (
              <MemoizedLazyComparisonAirplayTracking />
            ) : (
              <MemoizedLazyAirplayTracking />
            )}
          </Suspense>
        </AirplayWrapper>
      </DashboardContainer>

      {/* Enterprise plan limit modal */}
      <EnterprisePlanLimitModal
        showModal={modalControl.showEnterpriseModal}
        setShowModal={handleShowEnterpriseLimit}
      />

      {/* Enterprise extend-renew modal */}
      <EnterpriseExtendSongModal
        showModal={showEnterpriseExtend}
        setShowModal={setShowEnterpriseExtend}
      />
    </DashboardWrapper>
  );
};

export default Dashboard;
